import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type AuthTokensResponse, type Locale, type MeResponse, PERMISSIONS } from '@aps/contracts';
import { Button, IconLogo, LangSwitch, ThemeToggle, ToastProvider, type ThemeName } from '@aps/ui';
import { api, setTokens, setTokensChangedHandler } from '../lib/api.js';
import { type AdminSession, can, loadSession, saveSession } from '../lib/session.js';
import { messages } from '../i18n/messages.js';
import { MyDeliveriesScreen } from '../screens/MyDeliveriesScreen.js';
import { LoginScreen } from '../screens/LoginScreen.js';
import { WorkQueueScreen } from '../screens/WorkQueueScreen.js';
import { BucketScreen } from '../screens/BucketScreen.js';
import { OrdersScreen } from '../screens/OrdersScreen.js';
import { OrderScreen } from '../screens/OrderScreen.js';
import { PaymentsScreen } from '../screens/PaymentsScreen.js';
import { ProductsScreen } from '../screens/ProductsScreen.js';
import { ProductFormScreen } from '../screens/ProductFormScreen.js';
import { ImportScreen } from '../screens/ImportScreen.js';
import { ReferenceScreen } from '../screens/ReferenceScreen.js';
import { CategoriesScreen } from '../screens/CategoriesScreen.js';
import { ShopsScreen } from '../screens/ShopsScreen.js';
import { ZonesScreen } from '../screens/ZonesScreen.js';
import { StatusesScreen } from '../screens/StatusesScreen.js';
import {
  GarageStatsScreen,
  RegionStatsScreen,
  SearchLogScreen,
} from '../screens/AnalyticsScreen.js';
import { ConsignmentsScreen } from '../screens/ConsignmentsScreen.js';
import { DashboardScreen } from '../screens/DashboardScreen.js';
import { AuditScreen } from '../screens/AuditScreen.js';
import { UsersScreen } from '../screens/UsersScreen.js';
import { SessionContext, type SessionValue, useSession } from './session-context.js';
import { Sidebar } from './Sidebar.js';
import { type Navigation, type RootName, useNavigation } from './navigation.js';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // Admin ma'lumoti tez eskiradi (operator statusni o'zgartiradi),
      // lekin har fokusda so'rov yubormaymiz — ekranda o'ntagacha
      // kartochka bor va har biri alohida so'rov emas.
      staleTime: 15_000,
      retry: 1,
    },
  },
});

export function App(): ReactNode {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [locale, setLocale] = useState<Locale>('UZ');
  const [theme, setTheme] = useState<ThemeName>('dark');

  /**
   * Sahifa yangilanganda sessiyani tiklash.
   *
   * Saqlangan `me` ga ISHONILMAYDI — u eskirgan bo'lishi mumkin (rol
   * o'zgargan, foydalanuvchi o'chirilgan). Tokenlar qo'yilgach `me`
   * serverdan qayta o'qiladi; so'rov yiqilsa sessiya tozalanadi.
   */
  useEffect(() => {
    const stored = loadSession();
    if (stored === null) {
      setRestoring(false);
      return;
    }

    setTokens(stored.tokens);
    let cancelled = false;

    api
      .me()
      .then((me) => {
        if (cancelled) return;
        const fresh: AdminSession = { tokens: stored.tokens, me };
        saveSession(fresh);
        setSession(fresh);
      })
      .catch(() => {
        if (cancelled) return;
        setTokens(null);
        saveSession(null);
        setSession(null);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Refresh yangi tokenlar bergach ularni saqlab qo'yamiz. */
  useEffect(() => {
    setTokensChangedHandler((tokens) => {
      setSession((current) => {
        if (tokens === null) {
          saveSession(null);
          return null;
        }
        if (current === null) return null;
        const next: AdminSession = { tokens, me: current.me };
        saveSession(next);
        return next;
      });
    });
    return () => {
      setTokensChangedHandler(null);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset['theme'] = theme;
  }, [theme]);

  const signOut = useCallback(() => {
    setTokens(null);
    saveSession(null);
    setSession(null);
    // Boshqa foydalanuvchi kirganda oldingi ma'lumot ekranda qolmasin.
    client.clear();
  }, []);

  const onSignedIn = useCallback((tokens: AuthTokensResponse) => {
    setTokens(tokens);
    api
      .me()
      .then((me) => {
        const next: AdminSession = { tokens, me };
        saveSession(next);
        setSession(next);
      })
      .catch(() => {
        setTokens(null);
        setSession(null);
      });
  }, []);

  const value: SessionValue | null = useMemo(
    () => (session === null ? null : { me: session.me, locale, setLocale, signOut }),
    [session, locale, signOut],
  );

  if (restoring) return <main className="adm-login" />;

  if (value === null) {
    return (
      <QueryClientProvider client={client}>
        <LoginScreen locale={locale} onSignedIn={onSignedIn} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={client}>
      <SessionContext value={value}>
        {/* Bildirishnoma sessiya ICHIDA — xabar matni tanlangan tilda. */}
        <ToastProvider>
          <Shell theme={theme} onThemeChange={setTheme} />
        </ToastProvider>
      </SessionContext>
    </QueryClientProvider>
  );
}

/**
 * Kirgandan keyingi birinchi ekran: odatda boshqaruv paneli; faqat yetkazish ruxsati bor
 * (buyurtmalarni ko'rish yo'q) xodim — kuryer — o'z yetkazmalariga tushadi.
 */
export function landingRoot(me: MeResponse): RootName {
  return can(me, PERMISSIONS.DELIVERY_MY_SHIPMENTS) && !can(me, PERMISSIONS.ORDER_VIEW_ALL)
    ? 'deliveries'
    : 'dashboard';
}

interface ShellProps {
  theme: ThemeName;
  onThemeChange: (next: ThemeName) => void;
}

/**
 * Ilova qobig'i.
 *
 * `App` ICHIDA ta'riflanmaydi: ichkarida ta'riflangan komponent har
 * renderda yangi tip bo'lib qoladi, React uni butunlay qayta o'rnatadi
 * va navbat ichidagi holat (ochilgan guruh, sahifa kursori) yo'qoladi.
 */
function Shell({ theme, onThemeChange }: ShellProps): ReactNode {
  const { me, locale, setLocale, signOut } = useSession();
  // Kuryer boshqaruv panelini emas, o'z yetkazmalarini ko'radi (unda panel ruxsati yo'q).
  const navigation = useNavigation(landingRoot(me));
  const t = messages(locale);

  return (
    <div className="adm-shell">
      <header className="adm-topbar">
        <div className="adm-brand">
          <span className="adm-brand__mark">
            <IconLogo size={26} />
          </span>
          <div className="adm-brand__text">
            <span className="adm-brand__title">{t.appTitle}</span>
            <span className="adm-brand__subtitle">{t.appSubtitle}</span>
          </div>
        </div>

        <div className="adm-topbar__right">
          <span className="adm-user">{me.firstName ?? me.username ?? ''}</span>
          <LangSwitch value={locale} onChange={setLocale} />
          <ThemeToggle
            value={theme}
            onChange={onThemeChange}
            labels={{ dark: 'Dark', light: 'Light', race: 'Race', group: t.appSubtitle }}
          />
          <Button variant="ghost" size="sm" onClick={signOut}>
            {t.signOut}
          </Button>
        </div>
      </header>

      {/*
        Yon menyu va ekran — yonma-yon. Menyu HAR DOIM ko'rinadi:
        xodim boshqa ekranga o'tish uchun avval "orqaga" bosishi
        kerak bo'lsa, u ishni tashlab ketardi.
      */}
      <div className="adm-body">
        <Sidebar navigation={navigation} />
        <Screen navigation={navigation} />
      </div>
    </div>
  );
}

/**
 * Joriy yo'nalish bo'yicha ekran.
 *
 * `switch` — `Route` birlashmasi kengaysa `tsc` shu yerda yiqiladi
 * (`never` tekshiruvi), ekran unutilib qolmaydi.
 */
function Screen({ navigation }: { navigation: Navigation }): ReactNode {
  const route = navigation.route;

  switch (route.name) {
    case 'queue':
      return <WorkQueueScreen onOpenBucket={navigation.openBucket} />;
    case 'bucket':
      return <BucketScreen bucket={route.bucket} onBack={navigation.goBack} />;
    case 'orders':
      return <OrdersScreen onOpenOrder={navigation.openOrder} />;
    case 'order':
      return <OrderScreen orderId={route.orderId} onBack={navigation.goBack} />;
    case 'payments':
      return <PaymentsScreen onOpenOrder={navigation.openOrder} />;
    case 'products':
      return (
        <ProductsScreen
          onOpenProduct={navigation.openProduct}
          onCreateProduct={() => {
            navigation.openProduct(null);
          }}
        />
      );
    case 'import':
      return <ImportScreen />;
    case 'reference':
      return <ReferenceScreen />;
    case 'categories':
      return <CategoriesScreen />;
    case 'shops':
      return <ShopsScreen />;
    case 'zones':
      return <ZonesScreen />;
    case 'statuses':
      return <StatusesScreen />;
    case 'searchLog':
      return <SearchLogScreen />;
    case 'garageStats':
      return <GarageStatsScreen />;
    case 'regionStats':
      return <RegionStatsScreen />;
    case 'audit':
      return <AuditScreen />;
    case 'deliveries':
      return <MyDeliveriesScreen />;
    case 'users':
      return <UsersScreen />;
    case 'dashboard':
      return <DashboardScreen onNavigate={navigation.switchRoot} />;
    case 'consignments':
      return <ConsignmentsScreen onOpenOrder={navigation.openOrder} />;
    case 'product':
      return (
        <ProductFormScreen
          productId={route.productId}
          onBack={navigation.goBack}
          onCreated={navigation.replaceProduct}
        />
      );
    default: {
      const exhaustive: never = route;
      return exhaustive;
    }
  }
}
